import { EventStatus } from '../constants/eventStatus';
import { Ticket } from '../models/tickets';
import { CheckoutState } from '../state';
import {
    getScreenWidth,
    getUserAgent,
    isEventbriteTLDInDevOrQAEnv,
} from '../utils/browser';
import {
    hasRemainingQuantityDisplayEnabled,
    hasSomeTicketWithMoreThanOneVariant,
} from '../utils/tickets';
import {
    getCorrelationId,
    getIsRegEvent,
    getSessionId,
    hasFewTicketsLeftUrgencySignal,
    hasSalesEndSoonUrgencySignal,
    isAndroidApp,
    isEventbriteTLD,
    isIosApp,
} from './app';
import {
    getEventId,
    getEventStatus,
    getIsEventOnline,
    getIsFreeEvent,
    getIsReservedSeating,
    getOrganizerId,
    getSurveyType,
    getTicketReseller,
    getWaitlistSettings,
    isDonation,
    isListedEvent,
    isLockedEvent,
    isSeries,
    isSeriesParent,
} from './event';
import {
    hasQuestions,
    isApplePayAvailableAsPaymentMethod,
    isPaypalAvailableAsPaymentMethod,
} from './expressCheckout';
import { isLoginInterstitialSplitTreatmentEnabled } from './splitio';
import { getHasDonationTicketsAvailable, getTicketsById } from './tickets';
import { isAuthenticated, isUserLoggedIn } from './user';

export const isStatsigFreeTrialEnabledForInterstitialLogin = (
    state: CheckoutState,
): boolean =>
    (isEventbriteTLD(state) || isEventbriteTLDInDevOrQAEnv()) &&
    isLoginInterstitialSplitTreatmentEnabled(state);

export const isStatsigFreeTrialEnabled = (state: CheckoutState) =>
    isStatsigFreeTrialEnabledForInterstitialLogin(state);

export const getStatsigConfigFromState = (state: CheckoutState) => {
    const userAgent = getUserAgent() ?? {};
    const tickets: Ticket[] = Object.values(getTicketsById(state));
    const isFree = !!getIsFreeEvent(state);
    const isLoggedIn = isAuthenticated(state) || isUserLoggedIn(state);

    return {
        custom: {
            eventId: getEventId(state),
            isEventbriteTLD: isEventbriteTLD(state),
            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            hasMoreThanOneTicketType: tickets.length > 1,
            hasMultivariantTickets:
                hasSomeTicketWithMoreThanOneVariant(tickets),
            hasTicketResellers: !!getTicketReseller(state),
            hasDonationTicketsAvailable: getHasDonationTicketsAvailable(state),
            hasFewTicketsLeftUrgencySignal:
                hasFewTicketsLeftUrgencySignal(state),
            hasQuestions: hasQuestions(state),
            hasSalesEndSoonUrgencySignal: hasSalesEndSoonUrgencySignal(state),
            hasScreenWidth: getScreenWidth(),
            hasRemainingQuantityDisplay:
                hasRemainingQuantityDisplayEnabled(tickets),
            isDonation: isDonation(state),
            isFree: isFree,
            isLive: getEventStatus(state) === EventStatus.live,
            isLocked: isLockedEvent(state),
            isPaid: !isFree,
            isPaypalEPPAvailable: isPaypalAvailableAsPaymentMethod(state),
            isApplePayEPPAvailable: isApplePayAvailableAsPaymentMethod(state),
            isRegistrationEvent: !!getIsRegEvent(state),
            isReservedSeating: getIsReservedSeating(state),
            isSeries: isSeries(state),
            isSeriesParent: isSeriesParent(state),
            isUserLoggedIn: isLoggedIn,
            isWaitlist: !!getWaitlistSettings(state),
            listed: isListedEvent(state),
            onlineEvent: getIsEventOnline(state),
            surveyType: getSurveyType(state),
            useAndroidProfile: isAndroidApp(state),
            useIosProfile: isIosApp(state),
        },
        customIDs: {
            correlationId: getCorrelationId(state),
            eventId: getEventId(state),
            organizerId: getOrganizerId(state),
            sessionId: getSessionId(state),
        },
    };
};
